/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {ComponentFactoryResolver, ComponentRef, Directive, ViewContainerRef} from '@angular/core';
import {MESSAGE_TYPE} from '../ui';
import {isPresent} from '../utils/lang';
import {AlertModalComponent} from './alert-modal.component';
import {ConfirmModalComponent} from './confirm-modal.component';
import {DynamicConfirmModalComponent} from './dynamic-confirm-modal.component';
import {DialogModalComponent} from './dialog-modal.component';
import {Button, DialogModal, ModalDefinition} from './interfaces';
import {ModalService} from './modal.service';
import {HttpErrorResponse} from '@angular/common/http';
import {ApiErrorModalComponent} from './api-error-modal/api-error-modal.component';

/**
 * Modal reference
 */
class ModalRef {
  private _name: string;
  private _component: ComponentRef<any>;

  constructor(name: string, component: ComponentRef<any>) {
    this._name = name;
    this._component = component;
  }

  get name() {
    return this._name;
  }

  public destroy() {
    this._component.destroy();
  }
}

/**
 * Modal outlet component that loads and unloads modals on demand
 */
@Directive({
  selector: 'gp-modal-outlet'
})
export class ModalOutletComponent {

  private openModals: Array<ModalRef> = [];

  private modals: Array<ModalDefinition> = [];

  private stopGlobalListener: Function;

  constructor(private modalService: ModalService,
              private componentFactoryResolver: ComponentFactoryResolver,
              private viewContainerRef: ViewContainerRef) {
    this.modals = modalService.registerOutlet(this);
  }

  /**
   * Opens in the modal outlet the modal with passed name
   * @param name the name of the modal, as registered with @ModalConfig on the root app component, to open
   * @returns {Promise} a promise of the modal being opened
   */
  public openModal(name: string) {
    const modal = this.modals.find(c => c.name === name);
    if (modal === undefined) {
      throw new Error(`Modal with name ${name} not registered`);
    }

    const componentRef = this.viewContainerRef.createComponent(
      this.componentFactoryResolver.resolveComponentFactory(modal.component));

    this.openModals.push(new ModalRef(name, componentRef));
    this.modalService.modalOpened.emit(name);

    return new Promise((resolve, reject) => {
      // setTimeout(() => {
      //   this.stopGlobalListener = this.renderer.listen('document', 'click', event => {
      //     // Allow clicks outside a modal loaded in the outlet to close it
      //     if (isPresent(componentRef)) {
      //       const modalContent = componentRef.location.nativeElement.querySelector('.modal-content');
      //       if (!modalContent) {
      //         return;
      //       }
      //       if (!modalContent.contains(event.target)) {
      //         this.closeTopmostModal();
      //       }
      //     }
      //   });
      // });
      resolve(componentRef);
    });
  }

  /**
   * Dynamically load an alert modal in this modal outlet
   * @param options the alert modal options : allow to set title, text
   * @returns {Promise<void>} a promise resolving when user dismisses the modal
   */
  public alert(options: {
    title?: string;
    text?: string,
    size?: string,
    detail?: HttpErrorResponse | Error,
    customInfo?: {
      description?: string,
      message?: string
    },
    fancyDialogOptions?: {
      errorType?: MESSAGE_TYPE,
      description?: string
    },
  }): Promise<void> {
    const componentRef = this.viewContainerRef.createComponent(
      this.componentFactoryResolver.resolveComponentFactory(AlertModalComponent));

    (<AlertModalComponent>componentRef.instance).text = options.text;
    (<AlertModalComponent>componentRef.instance).title = options.title;
    (<AlertModalComponent>componentRef.instance).detail = options.detail;
    (<AlertModalComponent>componentRef.instance).customInfo = options.customInfo;
    (<AlertModalComponent>componentRef.instance).fancyDialogOptions = options.fancyDialogOptions;

    this.openModals.push(new ModalRef('alert', componentRef));
    this.modalService.modalOpened.emit('alert');

    return (<AlertModalComponent>componentRef.instance)
      .result
      .asObservable()
      .toPromise()
      .then(result => {
        this.closeTopmostModal();
        return undefined;
      });
  }

  /**
   * Dynamically load a confirm modal in this modal outlet
   * @param options the confirm modal options : allow to set title, text
   * @returns {Promise<boolean>} a promise resolving with the user choice
   */
  public confirm(
    options: { title?: string; text?: string, size?: string, customClass?: string, type?: 'danger' | 'warning' }
  ): Promise<boolean> {
    const componentRef = this.viewContainerRef.createComponent(
      this.componentFactoryResolver.resolveComponentFactory(ConfirmModalComponent));

    (<ConfirmModalComponent>componentRef.instance).text = options.text;
    (<ConfirmModalComponent>componentRef.instance).title = options.title;
    (<ConfirmModalComponent>componentRef.instance).size = options.size;
    (<ConfirmModalComponent>componentRef.instance).customClass = options.customClass;
    (<ConfirmModalComponent>componentRef.instance).type = options.type;

    this.openModals.push(new ModalRef('confirm', componentRef));
    this.modalService.modalOpened.emit('confirm');

    return (<ConfirmModalComponent>componentRef.instance).result
      .asObservable().toPromise().then(result => {
        this.closeTopmostModal();
        return result;
      });
  }

  public dynamicConfirmModal(options: {
    title?: string;
    text?: string,
    size?: string,
    customClass?: string,
    cancelButton?: string,
    confirmButton?: string,
    icon?: string,
    isCrossButtonVisible?: boolean,
    variant?: 'danger' | 'default'
  }): Promise<boolean> {
    const componentRef = this.viewContainerRef.createComponent(
      this.componentFactoryResolver.resolveComponentFactory(DynamicConfirmModalComponent));

    (<DynamicConfirmModalComponent>componentRef.instance).text = options.text;
    (<DynamicConfirmModalComponent>componentRef.instance).title = options.title;
    (<DynamicConfirmModalComponent>componentRef.instance).size = options.size;
    (<DynamicConfirmModalComponent>componentRef.instance).customClass = options.customClass;
    (<DynamicConfirmModalComponent>componentRef.instance).cancelButton = options.cancelButton;
    (<DynamicConfirmModalComponent>componentRef.instance).confirmButton = options.confirmButton;
    (<DynamicConfirmModalComponent>componentRef.instance).icon = options.icon;
    (<DynamicConfirmModalComponent>componentRef.instance).isCrossButtonVisible = options.isCrossButtonVisible;
    if (options.variant) {
      (<DynamicConfirmModalComponent>componentRef.instance).variant = options.variant;
    }

    this.openModals.push(new ModalRef('dynamicConfirmModal', componentRef));
    this.modalService.modalOpened.emit('dynamicConfirmModal');

    return (<DynamicConfirmModalComponent>componentRef.instance).result
      .toPromise().then(result => {
        this.closeTopmostModal();
        return result;
      });
  }

  public dialog(options: DialogModal): Promise<string> {
    const componentRef = this.viewContainerRef.createComponent(
      this.componentFactoryResolver.resolveComponentFactory(DialogModalComponent));

    (<DialogModalComponent>componentRef.instance).title = <string>options.title;
    (<DialogModalComponent>componentRef.instance).text = <string>options.text;
    (<DialogModalComponent>componentRef.instance).note = <string>options.note;
    (<DialogModalComponent>componentRef.instance).buttons = <Array<Button>>options.buttons;

    this.openModals.push(new ModalRef('dialog', componentRef));
    this.modalService.modalOpened.emit('dialog');

    return (<DialogModalComponent>componentRef.instance)
      .result
      .asObservable()
      .toPromise()
      .then(result => {
        this.closeTopmostModal();
        return result;
      });
  }

  public apiError(options: {
    type?: 'DANGER' | 'WARNING';
    title?: string;
    titleIcon?: string;
    description?: string,
    details?: string[],
    technicalDetailsForCopy?: string,
    withIllustration?: boolean
  }): Promise<void> {
    const componentRef = this.viewContainerRef.createComponent(
      this.componentFactoryResolver.resolveComponentFactory(ApiErrorModalComponent));

    const instance = <ApiErrorModalComponent>componentRef.instance;
    instance.type = options.type;
    instance.title = options.title;
    instance.titleIcon = options.titleIcon;
    instance.description = options.description;
    instance.details = options.details;
    instance.technicalDetailsForCopy = options.technicalDetailsForCopy;
    instance.withIllustration = options.withIllustration;

    this.openModals.push(new ModalRef('apiError', componentRef));
    this.modalService.modalOpened.emit('apiError');

    return instance
      .result
      .asObservable()
      .toPromise()
      .then(() => {
        this.closeTopmostModal();
        return undefined;
      });
  }

  /**
   * Closes the top-most displayed modal
   */
  public closeTopmostModal() {
    if (this.openModals.length > 0) {
      const closing = this.openModals.pop();
      closing.destroy();
      this.modalService.modalClosed.emit(closing.name);
      if (this.openModals.length === 0 && isPresent(this.stopGlobalListener)) {
        this.stopGlobalListener();
      }
    }
  }

  /**
   * Closes all modal popups
   */
  public closeAllModals() {
    if (this.openModals.length > 0) {
      this.openModals.forEach((modal) => {
        modal.destroy();
        this.modalService.modalClosed.emit(modal.name);
      });
      if (this.openModals.length === 0 && isPresent(this.stopGlobalListener)) {
        this.stopGlobalListener();
      }
    }
  }
}
